import { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, FileText, PauseCircle, MapPin } from 'lucide-react';
import { interfacesService, type OrderPreview, type StockComparison, type StockPreview } from '@/lib/interfacesService';
import type { FlowOptionValues } from '@/lib/interfacesCatalog';

const money = (n: number) => `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function OrdersPreview({ orders }: { orders: OrderPreview[] }) {
  if (!orders.length) return <p className="py-8 text-center text-sm text-gray-500">לא נמצאו הזמנות אחרונות בחנות.</p>;
  return (
    <div className="space-y-3">
      {orders.map(o => {
        const docs = o.outcome === 'issue' ? o.docs : o.suggestion;
        return (
          <div key={o.order_name} className="rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-gray-900">הזמנה {o.order_name}</p>
              {o.outcome === 'issue' ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  <FileText className="h-3.5 w-3.5" /> {docs.length > 1 ? `${docs.length} חשבוניות` : 'חשבונית אחת'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  <PauseCircle className="h-3.5 w-3.5" /> תיעצר להחלטה ידנית
                </span>
              )}
            </div>
            {o.existing_status && <p className="mt-1 text-xs text-gray-500">הזמנה זו כבר טופלה בעבר — לא תופק שוב.</p>}
            {!o.trigger_ready && !o.existing_status && <p className="mt-1 text-xs text-gray-500">עוד לא הגיעה לשלב שבו יוצאת חשבונית לפי ההגדרות.</p>}
            {o.split_reason && <p className="mt-2 text-xs text-gray-600">{o.split_reason}</p>}
            {!!o.shortage.length && (
              <ul className="mt-2 space-y-0.5 text-xs text-red-700">
                {o.shortage.map(s => <li key={s.sku + s.name}>חסרות {s.missing} יח' של {s.name}{s.sku ? ` (${s.sku})` : ''}</li>)}
              </ul>
            )}
            <div className="mt-3 space-y-2">
              {docs.map(d => (
                <div key={d.storageId} className="rounded-lg bg-gray-50 px-3 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-800">{o.outcome === 'issue' ? 'מסמך' : 'הצעה'} · {d.storageName}</span>
                    <span className="text-gray-700">{money(d.amount)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">{d.items.map(i => `${i.name} ×${i.quantity}`).join(' · ')}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const qty = (n: number) => Number.isInteger(n) ? String(n) : n.toLocaleString('he-IL', { maximumFractionDigits: 2 });

function ComparisonTable({ rows }: { rows: StockComparison[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 text-right font-medium">מוצר</th>
            <th className="px-3 py-2 text-right font-medium">מה רווחית שלחה</th>
            <th className="px-3 py-2 text-center font-medium">סה"כ רווחית</th>
            <th className="px-3 py-2 text-center font-medium">כרגע בחנות</th>
            <th className="px-3 py-2 text-center font-medium">יירשם בחנות</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(r => {
            const changed = r.from !== r.to;
            const itemIds = new Set(r.rivhit.map(s => s.item_id));
            return (
              <tr key={r.sku + r.title} className={changed ? 'bg-sky-50/40' : ''}>
                <td className="px-3 py-2 align-top"><p className="font-medium text-gray-800">{r.title}</p><p className="text-xs text-gray-500">{r.sku}</p></td>
                <td className="px-3 py-2 align-top text-xs">
                  {r.rivhit.length ? (
                    <ul className="space-y-0.5">
                      {r.rivhit.map((s, i) => (
                        <li key={i} className={s.counted ? 'text-gray-700' : 'text-gray-400 line-through'}>
                          {s.storage_name}: <span className="font-semibold">{qty(s.quantity)}</span>
                          {itemIds.size > 1 && <span className="text-gray-500"> · פריט {s.item_id}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-gray-400">—</span>}
                  {itemIds.size > 1 && <p className="mt-1 font-medium text-amber-700">יותר מפריט אחד ברווחית עם אותו מק"ט</p>}
                </td>
                <td className="px-3 py-2 text-center align-top text-gray-700">{qty(r.rivhit_total)}</td>
                <td className="px-3 py-2 text-center align-top text-gray-600">{r.from ?? '—'}</td>
                <td className={`px-3 py-2 text-center align-top font-semibold ${changed ? 'text-sky-700' : 'text-gray-500'}`}>{r.to}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StockPreviewView({ result }: { result: StockPreview }) {
  return (
    <div className="space-y-4">
      {result.location_name && (
        <p className="flex items-center gap-1.5 text-xs text-gray-600">
          <MapPin className="h-3.5 w-3.5 text-gray-400" />
          מיקום המלאי בשופיפיי שיתעדכן: <span className="font-semibold text-gray-800">{result.location_name}</span>
          {(result.locations_count ?? 0) > 1 && <span className="text-amber-700"> · בחנות יש {result.locations_count} מיקומי מלאי</span>}
        </p>
      )}
      <div className={`grid gap-3 text-center ${result.skipped_total ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
        <div className="rounded-xl bg-sky-50 p-3"><p className="text-xl font-bold text-sky-800">{result.changes_total}</p><p className="text-xs text-sky-700">יתעדכנו</p></div>
        <div className="rounded-xl bg-gray-50 p-3"><p className="text-xl font-bold text-gray-800">{result.unchanged}</p><p className="text-xs text-gray-600">ללא שינוי</p></div>
        <div className="rounded-xl bg-amber-50 p-3"><p className="text-xl font-bold text-amber-800">{result.unmatched_total}</p><p className="text-xs text-amber-700">לא נמצאו בחנות</p></div>
        {!!result.skipped_total && (
          <div className="rounded-xl bg-slate-100 p-3"><p className="text-xl font-bold text-slate-800">{result.skipped_total}</p><p className="text-xs text-slate-600">דולגו לפי הרשימה</p></div>
        )}
      </div>
      {result.comparison?.length ? (
        <>
          <ComparisonTable rows={result.comparison} />
          <p className="text-xs text-gray-500">מחסן מועבר בקו לא נספר, כי לא נבחר בהגדרות. שורות מודגשות ישתנו בחנות.</p>
        </>
      ) : !!result.changes.length && (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr><th className="px-3 py-2 text-right font-medium">מוצר</th><th className="px-3 py-2 text-center font-medium">כרגע בחנות</th><th className="px-3 py-2 text-center font-medium">יעודכן ל</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {result.changes.map(c => (
                <tr key={c.sku + c.title}>
                  <td className="px-3 py-2"><p className="font-medium text-gray-800">{c.title}</p><p className="text-xs text-gray-500">{c.sku}</p></td>
                  <td className="px-3 py-2 text-center text-gray-600">{c.from ?? '—'}</td>
                  <td className="px-3 py-2 text-center font-semibold text-sky-700">{c.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!result.comparison?.length && result.changes_total > result.changes.length && <p className="text-xs text-gray-500">מוצגים {result.changes.length} הראשונים מתוך {result.changes_total}.</p>}
      {(result.comparison?.length ?? 0) < result.matched && !!result.comparison?.length && <p className="text-xs text-gray-500">מוצגים {result.comparison.length} הראשונים מתוך {result.matched}.</p>}
      {!!result.unmatched.length && (
        <div>
          <p className="mb-1 text-sm font-semibold text-gray-800">מק"טים ברווחית שלא נמצאו בחנות</p>
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">{result.unmatched.join(', ')}</p>
        </div>
      )}
    </div>
  );
}

export function FlowPreviewModal({ kind, companyId, options, onClose }: {
  kind: 'orders' | 'stock';
  companyId: string;
  options: FlowOptionValues;
  onClose: () => void;
}) {
  const [orders, setOrders] = useState<OrderPreview[] | null>(null);
  const [stock, setStock] = useState<StockPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (kind === 'orders') {
        const r = await interfacesService.previewOrders(companyId, options);
        if (!alive) return;
        if (r.ok) setOrders(r.data); else setError(r.error);
      } else {
        const r = await interfacesService.previewStock(companyId, options);
        if (!alive) return;
        if (r.ok) setStock(r.data); else setError(r.error);
      }
    };
    void load();
    return () => { alive = false; };
  }, [kind, companyId, options]);

  const loading = !error && !orders && !stock;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-black/50 animate-in fade-in duration-200" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{kind === 'orders' ? 'תצוגה מקדימה — הזמנות אחרונות' : 'תצוגה מקדימה — עדכון מלאי'}</h3>
            <p className="mt-0.5 text-sm text-gray-500">בדיקה בלבד. שום מסמך לא מופק ושום מלאי לא מתעדכן.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="סגור"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && <p className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500"><Loader2 className="h-5 w-5 animate-spin" /> בודק מול החנות ורווחית...</p>}
          {error && <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-3 text-sm text-red-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</p>}
          {orders && <OrdersPreview orders={orders} />}
          {stock && <StockPreviewView result={stock} />}
        </div>
      </div>
    </div>
  );
}
